import { useEffect, useState } from "react";
import { useLang } from "@/context/LanguageContext";
import { Camera, MoveRight } from "lucide-react";
import { NALJURI_PHOTOS } from "@/lib/photos";

const CRICKET_BG = NALJURI_PHOTOS.VIRAL_CRICKET;

export default function CricketField() {
  const { t, lang } = useLang();
  const [scrollY, setScrollY] = useState(0);

  useEffect(() => {
    const onScroll = () => setScrollY(window.scrollY);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <section id="cricket" className="relative min-h-[80vh] overflow-hidden bg-forest-900 text-cream-100"
             data-testid="cricket-section">
      <div className="absolute inset-0">
        <div className="parallax-layer absolute inset-0"
             style={{ transform: `translate3d(0, ${(scrollY - 1200) * 0.12}px, 0) scale(1.1)` }}>
          <img src={CRICKET_BG} alt="Naljuri Viral Cricket Field — green clearing rimmed by hills"
               className="w-full h-[120%] object-cover" loading="lazy" />
        </div>
        <div className="absolute inset-0 bg-gradient-to-r from-forest-900/85 via-forest-900/55 to-forest-900/85" />
        <div className="absolute inset-0 grain-overlay" />
      </div>

      <div className="relative z-10 max-w-[1400px] mx-auto px-6 md:px-12 py-24 md:py-32 grid lg:grid-cols-12 gap-12 items-center min-h-[80vh]">
        <div className="lg:col-span-7">
          <p className="eyebrow text-gold-400 flex items-center gap-3" data-testid="cricket-eyebrow">
            <span className="inline-block w-7 h-px bg-gold-400/80" />
            {t.cricket.eyebrow}
          </p>
          <h2 className={`mt-5 font-display tracking-tight leading-[1.05] ${lang==='bn'?'font-bn':''}`}
              data-testid="cricket-title"
              style={{ fontSize: 'clamp(2.4rem, 5.6vw, 4.6rem)' }}>
            <span className="block text-cream-100">{t.cricket.title}</span>
          </h2>
          <p className="mt-7 text-base md:text-lg text-cream-100/85 max-w-2xl leading-relaxed">
            {t.cricket.sub}
          </p>
          <a href="#booking"
             data-testid="cricket-cta"
             className="mt-10 group inline-flex items-center gap-2 bg-gold-600 text-cream-100 px-7 py-3.5 rounded-full text-sm hover:bg-gold-700 transition-all hover:gap-4 border border-gold-500/40">
            <Camera className="w-4 h-4" /> {t.cricket.cta}
            <MoveRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
          </a>
        </div>

        <div className="lg:col-span-5 lg:col-start-8 grid grid-cols-3 gap-3">
          {t.cricket.stats.map((s, i) => (
            <div key={i} className="border border-cream-100/20 bg-forest-900/40 backdrop-blur-sm rounded-md p-5 text-center"
                 data-testid={`cricket-stat-${i}`}>
              <div className="text-2xl md:text-3xl font-display text-gold-400">{s.n}</div>
              <div className="text-[10px] uppercase tracking-widest2 mt-2 text-cream-100/65">{s.l}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
